import React, { useState, useMemo, useEffect } from "react";
import { Pressable, ScrollView, Platform } from "react-native";
import styled from "styled-components/native";
import { useDispatch } from "react-redux";
import {
  useSearchFiltersSlice,
  setPayloadData,
  setCollection,
  resetFiltersPayloadByProduct,
  removeCollection,
  setSelectedCollectionOption,
  clearSelectedCollectionOption,
  clearSubCollections,
} from "@redux/slices/searchFilters";
import { useTypographyStore } from "@redux/slices/typographySlice";
import { useTranslation } from "react-i18next";
import VDBText from "@components/atoms/Text";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import ReactDOM from "react-dom";
import RadioButton from "@components/atoms/RadioButton";
import { useTheme } from "styled-components/native";
import { DefaultTheme } from "styled-components/native";

interface CollectionOption {
  name: string;
  label: string;
  collection: string;
  sectionName?: string;
  predefined_payload: any;
  order?: number;
}

const syncCollectionQueryParam = (collection?: string) => {
  if (Platform.OS !== "web" || typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (collection) {
    if (url.searchParams.get("collection") === collection) return;
    url.searchParams.set("collection", collection);
  } else if (url.searchParams.has("collection")) {
    url.searchParams.delete("collection");
  } else {
    return;
  }
  window.history.replaceState(window.history.state, "", url.toString());
};

const isAllCollectionName = (name?: string) => {
  if (!name || typeof name !== "string") return false;
  const normalized = name.toLowerCase().replace(/_/g, "-").replace(/\s+/g, "-");
  return (
    normalized === "all-collections" ||
    normalized === "all-collection" ||
    normalized === "all-jewelry"
  );
};

const findMatchingCollectionOption = (
  options: CollectionOption[],
  collections: string[]
) => {
  const matches = options.filter((option) =>
    collections.includes(option.collection)
  );
  return (
    matches.find((option) => !isAllCollectionName(option.collection)) ||
    matches[0]
  );
};

interface CollectionDropdownProps {
  data: {
    name: string;
    type: string;
    label: string;
    options: CollectionOption[];
    payload_name: string[];
    platforms?: string[];
  };
  sectionName: string;
  selectedProduct: string;
  selectedStyle: any;
}

interface DropdownMenuProps {
  width?: number;
}

const CollectionDropdown: React.FC<CollectionDropdownProps> = ({
  data,
  sectionName,
  selectedProduct,
  selectedStyle,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const typography = useTypographyStore();
  const {
    collection,
    selectedCollectionOption,
    sectionName: activeSectionName,
  } = useSearchFiltersSlice();
  const [buttonRect, setButtonRect] = useState({ top: 0, left: 0, width: 0 });
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const theme = useTheme();

  const sortedOptions = useMemo(() => {
    if (!data?.options || !Array.isArray(data?.options)) return [];
    return [...data.options].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [data?.options]);

  const matchingCollectionOption = useMemo(
    () => findMatchingCollectionOption(sortedOptions, collection || []),
    [sortedOptions, collection]
  );

  // Prefer this dropdown's own options so Jewelry vs Lab-Grown labels
  // (both often keyed as "Rings") cannot leak across sections.
  const displayedCollectionLabel = useMemo(() => {
    if (matchingCollectionOption?.label) {
      return t(matchingCollectionOption.label);
    }

    if (selectedCollectionOption?.label) {
      const optionInThisDropdown = sortedOptions.find(
        (option) =>
          option.collection === selectedCollectionOption.collection ||
          option.name === selectedCollectionOption.name
      );
      if (optionInThisDropdown?.label) {
        return t(optionInThisDropdown.label);
      }
    }

    return t("Select Collection");
  }, [matchingCollectionOption, selectedCollectionOption, sortedOptions, t]);

  useEffect(() => {
    if (!collection?.length || !sortedOptions.length) return;
    // Desktop TabView mounts every section. Only the active tab should
    // own selectedCollectionOption, or Lab-Grown Rings overwrites Rings.
    if (activeSectionName && sectionName !== activeSectionName) return;

    const matchingOption = matchingCollectionOption;
    if (!matchingOption) return;

    const needsHydration =
      !selectedCollectionOption ||
      selectedCollectionOption.collection !== matchingOption.collection ||
      selectedCollectionOption.name !== matchingOption.name ||
      selectedCollectionOption.label !== matchingOption.label ||
      (!!matchingOption.predefined_payload &&
        !selectedCollectionOption.predefined_payload);

    if (needsHydration) {
      dispatch(
        setSelectedCollectionOption({
          name: matchingOption.name,
          label: matchingOption.label,
          collection: matchingOption.collection,
          sectionName: matchingOption.sectionName || sectionName,
          predefined_payload: matchingOption.predefined_payload,
        })
      );
    }
  }, [
    collection,
    selectedCollectionOption,
    sortedOptions,
    matchingCollectionOption,
    sectionName,
    activeSectionName,
    dispatch,
  ]);

  const handleSelectCollection = (option: CollectionOption) => {
    const isCurrentlySelected =
      matchingCollectionOption?.collection === option.collection;

    if (isCurrentlySelected) {
      dispatch(removeCollection(option.collection));
      dispatch(clearSelectedCollectionOption());
      dispatch(clearSubCollections());
      syncCollectionQueryParam();
    } else {
      dispatch(resetFiltersPayloadByProduct(selectedProduct));
      dispatch(clearSubCollections());

      dispatch(
        setPayloadData({
          productType: selectedProduct,
          data: {
            [option.sectionName || sectionName]: option.predefined_payload,
          },
        })
      );

      // Radio selection replaces the list. All-Jewelry stays in redux as a
      // parent, so appending Rings/Earrings would leave them unselectable.
      dispatch(setCollection([option.collection]));
      syncCollectionQueryParam(option.collection);
      dispatch(
        setSelectedCollectionOption({
          name: option.name,
          label: option.label,
          collection: option.collection,
          sectionName: option.sectionName,
          predefined_payload: option.predefined_payload,
        })
      );
    }

    setIsDropdownOpen(false);
  };

  const handleButtonClick = () => {
    if (Platform.OS === "web" && containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setButtonRect({
        top: rect.bottom,
        left: rect.left,
        width: rect.width,
      });
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  const renderDropdownContent = () => {
    if (!isDropdownOpen) return null;

    const dropdownContent = (
      <DropdownPortalContent>
        <DropdownOverlay onPress={() => setIsDropdownOpen(false)} />
        <DropdownMenu
          style={{
            top: buttonRect.top,
            left: buttonRect.left,
          }}
          width={buttonRect.width}
        >
          <ScrollView
            style={{ maxHeight: 200 }}
            showsVerticalScrollIndicator={false}
          >
            {sortedOptions.map((option, index) => {
              const isSelected =
                matchingCollectionOption?.collection === option.collection;
              return (
                <React.Fragment key={option.collection}>
                  <DropdownItem
                    onPress={() => handleSelectCollection(option)}
                    isSelected={isSelected}
                    theme={selectedStyle}
                    disabled={isSelected}
                  >
                    <ItemContent>
                      <RadioButton
                        checked={isSelected}
                        onPress={() => handleSelectCollection(option)}
                        margin="0"
                        disabled={isSelected}
                      />
                      <VDBText
                        color={theme["core-05"]}
                        type={typography["Body-M"]}
                      >
                        {t(option.label)}
                      </VDBText>
                    </ItemContent>
                  </DropdownItem>
                  {index < sortedOptions.length - 1 && <Separator />}
                </React.Fragment>
              );
            })}
          </ScrollView>
        </DropdownMenu>
      </DropdownPortalContent>
    );

    if (Platform.OS === "web" && typeof document !== "undefined") {
      return ReactDOM.createPortal(dropdownContent, document.body);
    }

    return dropdownContent;
  };

  return (
    <Container theme={selectedStyle}>
      <DropdownContainer ref={containerRef as any}>
        <DropdownButton
          onPress={handleButtonClick}
          isOpen={isDropdownOpen}
          theme={selectedStyle}
        >
          <TextContainer>
            <VDBText type={typography["Minor-R"]} color={theme["core-06"]}>
              {t(data.label)}
            </VDBText>
            <VDBText type={typography["Body-SB"]} color={theme["core-05"]}>
              {displayedCollectionLabel}
            </VDBText>
          </TextContainer>
          <IconContainer isOpen={isDropdownOpen}>
            <VDBIcon
              name={VDBIcons.ARROW_DOWN}
              size={20}
              color={selectedStyle?.secondary}
              variant={IconsVariant.VDBCustom}
            />
          </IconContainer>
        </DropdownButton>
        {renderDropdownContent()}
      </DropdownContainer>
    </Container>
  );
};

const Container = styled.View`
  gap: ${SpaceType.Small}px;
`;

const DropdownContainer = styled.View`
  position: relative;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
`;

const DropdownButton = styled(Pressable)<{ isOpen: boolean }>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: ${SpaceType.Basic}px;
  padding-bottom: ${SpaceType.Basic}px;
  padding-left: ${SpaceType.Extra}px;
  padding-right: ${SpaceType.Extra}px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-01"]};
  border: 1px solid ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  border-radius: ${SpaceType.Basic}px;
  border-color: ${({
    theme,
    isOpen,
  }: {
    theme: DefaultTheme;
    isOpen: boolean;
  }) => (isOpen ? theme["core-03"] : theme["core-03"])};
`;

const TextContainer = styled.View`
  flex: 1;
  flex-direction: column;
  align-items: flex-start;
  gap: ${SpaceType.Basic}px;
`;

const IconContainer = styled.View<{ isOpen: boolean }>`
  transform: ${({ isOpen }: { isOpen: boolean }) =>
    isOpen ? "rotate(180deg)" : "rotate(0deg)"};
  transition-duration: 200ms;
`;

const DropdownPortalContent = styled.View`
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
`;

const DropdownOverlay = styled(Pressable)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9998;
  background-color: transparent;
  pointer-events: auto;
`;

const DropdownMenu = styled.View<DropdownMenuProps>`
  position: absolute;
  z-index: 9999;
  background-color: white;
  border: 1px solid #ccc;
  border-radius: 8px;
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  width: ${({ width }: { width: number }) => width}px;
  pointer-events: auto;
`;

const DropdownItem = styled(Pressable)<{ isSelected: boolean }>`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Double}px;
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
  background-color: ${({
    theme,
    isSelected,
  }: {
    theme: DefaultTheme;
    isSelected: boolean;
  }) => (isSelected ? theme["core-02"] : "white")};
`;

const Separator = styled.View`
  height: 1px;
  background-color: ${({ theme }: { theme: DefaultTheme }) => theme["core-03"]};
  width: 100%;
`;

const ItemContent = styled.View`
  flex-direction: row;
  align-items: center;
  flex: 1;
`;

export default CollectionDropdown;
